import qs from 'qs';
import request from '@medlinker/apitool/request';

import env from '../utils/env';
import app from '../../app/app';
import history from '../../app/history';
import { ApiNetError, ApiBusinessError, ApiCancelError } from '../errors';

const getLoginUrl = () => {
  return history.createHref({
    pathname: `/login?redirectUrl=${encodeURIComponent(window.location.href)}`,
  });
};

export const SESS_KEY = 'ih-query-sess';
const PLATFORM_KEY = 'ih-query-platform';

export default request.create({
  withCredentials: true,

  baseURL: process.env.MED_PATIENT_API_HOST,
  beforeSend(config) {
    const headers = config.headers || {};
    const originQuery = qs.parse(window.location.search);
    const isJiaxiaoYi = originQuery.source?.toString().startsWith('jxy');
    const query = qs.parse(window.location.search.slice(1));

    if (query._opentype && env.isWechat && !isJiaxiaoYi) {
      if (!config.noOpenType) {
        headers['X-Wechat-Type'] = query._opentype;
      }
    }

    const params = config.params || {};

    // 仅支持小程序webview，定位服务端那边没支持取header
    if (query._opentype && env.isWechatMiniProgram && !isJiaxiaoYi) {
      params['wechatType'] = query._opentype;
    }

    // 家小医不要wechatType
    if (env.isWechatMiniProgram && isJiaxiaoYi) {
      params['mwechatType'] = 'null';
    }

    // 未来医生小程序内嵌H5需要
    function singleQuery(val: unknown): string {
      if (Array.isArray(val)) return val[0] ?? '';
      if (typeof val === 'string') return val;

      return '';
    }
    const xWechatType =
      singleQuery(query.xWechatType) ||
      sessionStorage.getItem('xWechatType-toc-h5');

    if (xWechatType && !isJiaxiaoYi) {
      sessionStorage.setItem('xWechatType-toc-h5', xWechatType);
      headers['x-wechat-type'] = xWechatType;
    }

    if (/medgpt_/.test(String(query.channel))) {
      config.skipLogin = true;
    }

    // @ts-ignore
    params['_q'] = Math.random().toFixed(8);

    let sess = '';
    let platform = '';

    if (query.sess) {
      sess = query.sess as string;
      sessionStorage.setItem(SESS_KEY, sess);
    } else {
      sess = sessionStorage.getItem(SESS_KEY) || '';
    }
    // 如果query携带uid，把uid带入params
    if (query.uid) {
      params.uid = query.uid;
    }
    if (query.platform) {
      platform = query.platform as string;
      sessionStorage.setItem(PLATFORM_KEY, platform);
    } else {
      platform = sessionStorage.getItem(PLATFORM_KEY) || '';
    }
    if (sess) {
      params.sess = sess;
      params.msess = sess;
    }
    if (platform) {
      params.platform = platform;
      params.mplatform = platform;
      params.x_platform = platform;
    }

    if (window.medhybrid?.core.getEnv().hybrid && !sess) {
      return window.medhybrid?.auth
        .checkSession()
        .then(({ logined, sessInfo }) => {
          if (logined && sessInfo) {
            sessionStorage.setItem(SESS_KEY, sessInfo.sess || '');
            sessionStorage.setItem(PLATFORM_KEY, sessInfo.x_platform || '');
            params.sess = sessInfo.sess;
            params.msess = sessInfo.sess;
            params.platform = sessInfo.x_platform;
            params.mplatform = sessInfo.x_platform;
            params.x_platform = sessInfo.x_platform;
          }

          return {
            ...config,
            params,
            headers,
          };
        })
        .catch(() => ({
          ...config,
          params,
          headers,
        }));
    }

    return {
      ...config,
      params,
      headers,
    };
  },
  onSuccess(res, config) {
    const { data } = res;

    // 接口正常，返回值
    if (data.errcode === 0 || data.code === 0 || data.code === 200) {
      if (config.sr) {
        return res;
      }

      return data.data;
    }

    const isNewApi = config.baseURL!.includes('patient-medication');
    const query = qs.parse(window.location.search.slice(1));
    // 添加城市选择登录逻辑判断
    const isUserHistoryCity =
      config.url! === '/user-interface/user/patient/GetUserAreasHistory';

    if (config.skipLogin !== true) {
      const loginUrl = getLoginUrl();

      if (
        ((!isNewApi && [233, 3, 101].includes(data.errcode)) ||
          (isNewApi && [101, 102].includes(data.errcode)) ||
          (isUserHistoryCity &&
            [401].includes(data.errcode) &&
            !env.isWechatMiniProgram)) &&
        config.skipLogin !== true
      ) {
        // 重置
        app.getStore()!.dispatch.app.clearUserContext({});
        sessionStorage.setItem(SESS_KEY, '');
        sessionStorage.setItem(PLATFORM_KEY, '');
        if (env.isWechat && query._opentype) {
          let scopeType: GetWechatAuthPathScope = 1;

          // scopeType默认是1，如果针对部分请求需要变化，可以通过api().setConfig({scopeType: 1}) 设置
          if (config.scopeType)
            scopeType = config.scopeType as GetWechatAuthPathScope;
          window.location.replace(
            getWechatAuthPath(
              (query._opentype as string) || '',
              window.location.href,
              scopeType
            )
          );
        } else if (window.medhybrid?.core.getEnv().hybrid) {
          window.medhybrid.auth.logout().then(() => {
            console.log('退出APP登录成功');
          });
        } else {
          window.location.replace(loginUrl);
        }
      } else if (
        ((!isNewApi && [70000, 100003, 401].includes(data.errcode)) ||
          (isNewApi && [103, 105].includes(data.errcode))) &&
        config.skipLogin !== true
      ) {
        // 重置
        app.getStore()!.dispatch.app.clearUserContext({});

        sessionStorage.setItem(SESS_KEY, '');
        sessionStorage.setItem(PLATFORM_KEY, '');
        if (window.medhybrid?.core.getEnv().hybrid) {
          window.medhybrid.auth.logout().then(() => {
            console.log('退出APP登录成功');
          });
        } else {
          window.location.replace(loginUrl);
        }
      }
    }

    const code = data.errcode || data.code;
    const msg =
      /medgpt_/.test(String(query.channel)) &&
      [102, 401, 180103002].includes(code)
        ? `出错了【${code}】`
        : data.errmsg || data.msg;

    const apiError = new ApiBusinessError(msg, code, data.data);

    return Promise.reject(apiError);
  },
  onError(error, config) {
    if (error instanceof ApiCancelError && !error.isAxiosError) {
      return Promise.reject(error);
    }

    const { response } = error;
    let errmsg = '请求失败，请稍后再试';
    let errcode = -255;

    if (response) {
      const { status } = response;

      errcode = status;

      // 401 跳转登录
      if (config.skipLogin !== true && errcode === 401) {
        const query = qs.parse(window.location.search.slice(1));
        const loginUrl = getLoginUrl();

        // 重置
        app.getStore()!.dispatch.app.clearUserContext({});
        if (env.isWechat && query._opentype) {
          window.location.replace(
            getWechatAuthPath(
              query._opentype as string,
              window.location.href,
              1
            )
          );
        } else if (window.medhybrid?.core.getEnv().hybrid) {
          window.medhybrid.auth.logout().then(() => {
            console.log('退出APP登录成功');
          });
        } else {
          window.location.replace(loginUrl);
        }
      }

      switch (status) {
        case 0:
          errmsg = `请求失败，请检查网络:${errcode}`;
          break;
        case 404:
          errmsg = `您请求的地址不存在:${errcode}`;
          break;
        default:
          errmsg = `服务异常，请稍后再试:${errcode}`;
      }
    } else {
      errmsg = '请求失败，请检查网络';
    }

    const apiError = new ApiNetError(errmsg, errcode);

    return Promise.reject(apiError);
  },
});

/**
 * scope参数说明
 * ｜ 请求scopeType ｜    微信授权 scope       ｜系统操作｜
｜        1                 ｜snsapi_base                ｜静默获取 openid ｜
｜        2                 ｜snsapi_userinfo           ｜弹授权框，获取 openid + 用户信息（含unionID) ｜
｜        3                 ｜snsapi_base                ｜静默获取 openid + unionID |
*/
export type GetWechatAuthPathScope = 1 | 2 | 3;
export const getWechatAuthPath = (
  opentype: string | string[],
  currentUrl: string,
  scope: GetWechatAuthPathScope
) => {
  let wxPlatform = opentype as string;
  let redirectUrl = currentUrl;

  if (Array.isArray(opentype)) {
    const [path, querystring] = currentUrl.split('?');
    const query = qs.parse(querystring);

    wxPlatform = opentype[opentype.length - 1];
    redirectUrl = `${path}${qs.stringify(
      { ...query, _opentype: wxPlatform },
      { addQueryPrefix: true }
    )}`;
  }
  const wxAccessUrl = `${process.env.MED_AUTH_HOST}/auth/bind`;
  const wxAccessQueries = `?wxPlatform=${wxPlatform}&redirectUrl=${encodeURIComponent(
    redirectUrl
  )}&scope=${scope}`;

  return `${wxAccessUrl}${wxAccessQueries}`;
};
